<!--
  - Copyright (c) ProviderTrust LLC.
  - All Rights Reserved.
  -
  - This software is confidential and proprietary information of
  - ProviderTrust LLC ("Confidential Information"). You shall not disclose
  - such Confidential Information and shall use it only in
  - accordance with the terms of the license agreement you entered
  - into with ProviderTrust.
-->

<template>
  <div class="flex-search-details-exclusions">
    <div class="operations-card">
      <div class="left-card">
        <div class="form-group">
          <div class="btn-group">
            <v-btn class="icon-btn" color="white" @click="toggleCollapse">
              <v-icon color="primary"
                >fa-{{ collapseAll ? "plus" : "minus" }}-circle</v-icon
              >
            </v-btn>

            <ec-dropdown>
              <v-btn class="icon-btn" color="white" slot="activator">
                <v-icon color="primary">fa-columns</v-icon>
              </v-btn>

              <div class="dropdown-wrapper app-select-options">
                <h3>Select Columns to Display</h3>

                <v-select-list
                  :items="headerOptions"
                  action
                  :selected-items="selectedHeaderValues"
                  color="primary"
                  @select="updateSelectedHeaderValues"
                />
              </div>
            </ec-dropdown>

            <v-btn
              ref="testSortBtn"
              class="icon-btn"
              color="white"
              @click="
                searchCriteriaSortDescending = !searchCriteriaSortDescending
                debouncedSearch(true)
              "
            >
              <v-icon color="primary"
                >fa-sort-alpha-{{
                  searchCriteriaSortDescending | sortName
                }}</v-icon
              >
            </v-btn>
          </div>
        </div>
        <div class="form-group">
          <label>Search criteria</label>
          <v-text-field
            class="filter search-filter"
            v-model="searchCriteriaFilter"
            @input="debouncedSearch(true)"
            solo
          />
        </div>
        <div class="form-group">
          <label>Record status</label>
          <v-select
            :items="statusFilterOptions"
            v-model="statusFilter"
            class="filter"
            content-class="app-select-options"
            multiple
            @input="debouncedSearch(true)"
            solo
          >
            <template slot="selection" slot-scope="{ item, index }">
              <v-chip v-if="index === 0">
                <span>{{ item.text }}</span>
              </v-chip>
              <span v-if="index === 1" class="grey--text caption"
                >(+{{ statusFilter.length - 1 }} others)</span
              >
            </template>
          </v-select>
        </div>
        <div class="form-group">
          <label>Source</label>
          <v-select
            :items="sources"
            v-model="sourcesFilter"
            class="filter"
            content-class="app-select-options"
            multiple
            @input="debouncedSearch(true)"
            ref="testVSelectSources"
            solo
          >
            <template slot="selection" slot-scope="{ item, index }">
              <v-chip v-if="index === 0">
                <span>{{ item }}</span>
              </v-chip>
              <span v-if="index === 1" class="grey--text caption"
                >(+{{ sourcesFilter.length - 1 }} others)</span
              >
            </template>
          </v-select>
        </div>
        <v-btn color="primary" class="reset-btn" @click="reset"> Reset </v-btn>
      </div>
      <status-dropdown-dialog
        class="bulk-status-dropdown"
        :disabled="!selectedItems.length"
        @status-change="
          saveBulkStatusChange(
            $event,
            API.FLEXBATCH_SANCTION_DETAILS_BYIDS,
            data => data.records
          )
        "
        :status-infos="nameStatusInfos"
        :status-change-loading="statusChangeLoading"
      >
        Bulk Status Change
      </status-dropdown-dialog>
    </div>

    <v-data-table
      ref="sanctiontable"
      class="ec-table exclusion-table"
      v-model="selectedItems"
      :headers="filteredSelectedHeaders"
      :items="items"
      item-key="id"
      select-all
      sort-icon="arrow_drop_up"
      :group-key="groupKey"
      group-expanded
      :group-sort-descending="groupSortDescending"
      expand-icon="keyboard_arrow_right"
      hide-actions
    >
      <template slot="group" slot-scope="props">
        <table class="groupinfotable">
          <tr>
            <th :class="{ 'last-column': !groupUniqueId(props.groupName) }">
              Search Criteria
            </th>
            <th v-show="groupUniqueId(props.groupName)">Unique ID</th>
          </tr>
          <tr>
            <td :class="{ 'last-column': !groupUniqueId(props.groupName) }">
              {{ groupSearchCriteria(props.groupName) | upper }}
            </td>
            <td v-show="groupUniqueId(props.groupName)">
              {{ groupUniqueId(props.groupName) }}
            </td>
          </tr>
        </table>

        <div class="status-cards">
          <span
            :class="[status.class, 'status-card']"
            :key="status.text"
            v-for="status in getGroupStatus(props.groupName)"
            >{{ status.text }}</span
          >
        </div>
      </template>

      <template slot="items" slot-scope="props">
        <tr
          :class="{ 'even-row-in-group': props.itemInGroupIndex % 2 === 0 }"
          @click="updateSlideIn(props.item.id)"
          :key="props.item.id"
        >
          <td @click.stop>
            <v-checkbox color="primary" v-model="props.selected" hide-details />
          </td>
          <td>
            <div class="table-cell-wrapper">{{ props.item.name }}</div>
          </td>
          <td>
            <div class="table-cell-wrapper">{{ props.item.companyName }}</div>
          </td>
          <td v-show="isHeaderSelected('source')">
            <a
              v-if="props.item.link !== ''"
              :href="props.item.link"
              target="_blank"
              @click.capture.stop
              >{{ props.item.source }}
            </a>
            <div v-else class="table-cell-wrapper">{{ props.item.source }}</div>
          </td>
          <td v-show="isHeaderSelected('state')">{{ props.item.state }}</td>
          <td v-show="isHeaderSelected('exclusionDate')">
            {{ props.item.exclusionDate }}
          </td>
          <td v-show="isHeaderSelected('exclusionType')">
            {{ props.item.exclusionType }}
          </td>
          <td v-show="isHeaderSelected('status')" class="dropdown-column">
            <status-dropdown-dialog
              @click.capture.stop.prevent
              :status="props.item.status"
              :bottom-up-dropdown="bottomUpDropdowns[props.index]"
              :bottom-up-confirmdialog="bottomUpConfirmdialogs[props.index]"
              @show-dropdown="
                updateBottomUp(
                  $event,
                  'bottomUpDropdowns',
                  props.index,
                  tableQuery
                )
              "
              @show-confirmdialog="
                updateBottomUp(
                  $event,
                  'bottomUpConfirmdialogs',
                  props.index,
                  tableQuery
                )
                hasConfirmdialogOpened = $event
              "
              :force-dropdown-closed="hasConfirmdialogOpened"
              @status-change="
                saveStatusChange(
                  $event,
                  [props.item.id],
                  API.FLEXBATCH_SANCTION_DETAILS_BYIDS,
                  data => data.records
                )
              "
              :status-infos="nameStatusInfos"
            />
          </td>
          <td v-show="isHeaderSelected('latestNote')">
            {{ props.item.latestNote }}
          </td>
          <td v-show="isHeaderSelected('akas')">{{ props.item.akas }}</td>
          <td v-show="isHeaderSelected('dbas')">{{ props.item.dbas }}</td>
          <td v-show="isHeaderSelected('middleName')">
            {{ props.item.middleName }}
          </td>
          <td v-show="isHeaderSelected('prefix')">{{ props.item.prefix }}</td>
          <td v-show="isHeaderSelected('suffix')">{{ props.item.suffix }}</td>
          <td v-show="isHeaderSelected('dateOfBirth')">
            {{ props.item.dateOfBirth }}
          </td>
          <td v-show="isHeaderSelected('startDate')">
            {{ props.item.startDate }}
          </td>
          <td v-show="isHeaderSelected('classification')">
            {{ props.item.classification }}
          </td>
          <td v-show="isHeaderSelected('address')">{{ props.item.address }}</td>
          <td v-show="isHeaderSelected('city')">{{ props.item.city }}</td>
          <td v-show="isHeaderSelected('county')">{{ props.item.county }}</td>
          <td v-show="isHeaderSelected('postalCode')">
            {{ props.item.postalCode }}
          </td>
          <td v-show="isHeaderSelected('country')">{{ props.item.country }}</td>
          <td v-show="isHeaderSelected('NPI')">{{ props.item.NPI }}</td>
          <td v-show="isHeaderSelected('specialty')">
            {{ props.item.specialty }}
          </td>
          <td v-show="isHeaderSelected('licenseNumbers')">
            {{ props.item.licenseNumbers }}
          </td>
          <td v-show="isHeaderSelected('providerNumbers')">
            {{ props.item.providerNumbers }}
          </td>
          <td v-show="isHeaderSelected('caseNumber')">
            {{ props.item.caseNumber }}
          </td>
          <td v-show="isHeaderSelected('exclusionTerm')">
            {{ props.item.exclusionTerm }}
          </td>
          <td v-show="isHeaderSelected('comments')">
            {{ props.item.comments }}
          </td>
          <td v-show="isHeaderSelected('dateAcquiredFromSource')">
            {{ props.item.dateAcquiredFromSource }}
          </td>
        </tr>
      </template>
    </v-data-table>
    <div class="text-xs-center pt-2">
      <v-pagination v-model="page" :length="pages" :total-visible="7" />
    </div>
    <!--
      it is hard to believe, but the following unused component can NOT be deleted for a hard to understand fact: it prevents
      an unrelated unit test from failing...
    -->
    <v-navigation-drawer temporary />
  </div>
</template>

<script>
import {
  API,
  createFilterList,
  nameStatusInfos,
  RECORDS_PER_PAGE,
  SEARCH_DEBOUNCE_TIMEOUT
} from "@/common/constants"
import axios from "axios"
import { debounce } from "lodash"
import { mapActions, mapMutations, mapState } from "vuex"
import StatusDropdownDialog from "@/components/StatusDropdownDialog"
import EcDropdown from "@/components/Dropdown"
import EventBus from "@/common/event-bus"
import { deleteElFromArry } from "@/service/util"
import { sortName, upper } from "@/service/filters"
import saveStatusChange from "@/mixins/statusChange"
import flipDialogUp from "@/mixins/flipDialogUp"

const thePromise = Promise.resolve()

const initShowingHeaders = [
  {
    value: "source",
    width: "200px",
    text: "Source"
  },
  {
    value: "state",
    width: "200px",
    text: "State"
  },
  {
    value: "exclusionDate",
    width: "200px",
    text: "Exclusion Date"
  },
  {
    value: "exclusionType",
    width: "300px",
    text: "Exclusion Type"
  },
  {
    value: "status",
    width: "200px",
    text: "Status"
  },
  {
    value: "latestNote",
    width: "350px",
    text: "Latest Note"
  }
]

const initNotShowingHeaders = [
  {
    value: "akas",
    width: "300px",
    text: "AKAs"
  },
  {
    value: "dbas",
    width: "300px",
    text: "DBAs"
  },
  {
    value: "middleName",
    width: "200px",
    text: "Middle Name"
  },
  {
    value: "prefix",
    width: "200px",
    text: "Prefix"
  },
  {
    value: "suffix",
    width: "200px",
    text: "Suffix"
  },
  {
    value: "dateOfBirth",
    width: "200px",
    text: "Date of Birth"
  },
  {
    value: "startDate",
    width: "200px",
    text: "Start Date"
  },

  {
    value: "classification",
    width: "300px",
    text: "Classification"
  },
  {
    value: "address",
    width: "300px",
    text: "Address"
  },
  {
    value: "city",
    width: "200px",
    text: "City"
  },
  {
    value: "county",
    width: "200px",
    text: "County"
  },
  {
    value: "postalCode",
    width: "200px",
    text: "Postal Code"
  },
  {
    value: "country",
    width: "200px",
    text: "Country"
  },
  {
    value: "NPI",
    width: "300px",
    text: "NPI"
  },
  {
    value: "specialty",
    width: "300px",
    text: "Specialty"
  },
  {
    value: "licenseNumbers",
    width: "300px",
    text: "License Numbers"
  },
  {
    value: "providerNumbers",
    width: "300px",
    text: "Provider Numbers"
  },
  {
    value: "caseNumber",
    width: "200px",
    text: "Case Number"
  },
  {
    value: "exclusionTerm",
    width: "300px",
    text: "Exclusion Term"
  },
  {
    value: "comments",
    width: "300px",
    text: "Comments"
  },
  {
    value: "dateAcquiredFromSource",
    width: "300px",
    text: "Date Acquired From Source"
  }
]

const availableHeaders = initShowingHeaders.concat(initNotShowingHeaders)

const eventModifier = "NAME"

export default {
  name: "FlexSearchDetailsExclusions",
  displayName: "Exclusions",

  props: {
    batchId: {
      type: String,
      default: "-1"
    }
  },

  data: () => ({
    page: 0,
    pages: 1,

    groupKey: "searchCriteriaKey",
    collapseAll: false,
    items: [],
    selectedItems: [],

    searchCriteriaFilter: "",
    statusFilter: [],
    sourcesFilter: [],

    bottomUpDropdowns: [],
    bottomUpConfirmdialogs: [],
    hasConfirmdialogOpened: false,
    nameStatusInfos,
    tableQuery: "table.fixed-columns-table",
    API,
    statusChangeText: "",
    selectedStatus: null,

    currentSlideInId: -1,

    headerOptions: availableHeaders,
    selectedHeaderValues: initShowingHeaders.map(({ value }) => value),

    searchCriteriaSortDescending: false, // for this component
    groupSortDescending: false, // for the table

    groups: {},

    fixedColumnsTable: true
  }),

  beforeDestroy() {
    EventBus.$off("global-status-change" + eventModifier)
  },
  created() {
    this.search(true)

    this.getSources() // TODO figure out how to 'debounce' when it is used in more than one place

    EventBus.$on("global-status-change" + eventModifier, evt => {
      const id = this.currentSlideInId
      if (id !== -1) {
        this.saveStatusChange(
          evt,
          [id],
          API.FLEXBATCH_SANCTION_DETAILS_BYIDS,
          data => data.records
        ).then(() => this.updateSlideIn(id))
      }
    })
  },
  watch: {
    page(oldVal, newVal) {
      if (!this.loading) this.search()
    }
  },
  mixins: [saveStatusChange, flipDialogUp],
  methods: {
    ...mapMutations([
      "setSlideIn",
      "setStatusInfos",
      "hasClearNonBtn",
      "setEventModifier"
    ]),
    ...mapActions(["getSources"]),

    toggleCollapse() {
      this.collapseAll = !this.collapseAll
      this.collapseAll
        ? this.$refs.sanctiontable.collapseAll()
        : this.$refs.sanctiontable.expandAll()
    },
    getGroupStatus(groupKey) {
      const statusInfos = this.groups[groupKey].status
      const result = []
      let count

      if (
        (count =
          (statusInfos["NONCLEARED"] || 0) + (statusInfos["UNDER_REVIEW"] || 0))
      ) {
        result.push({
          class: "statuswarn",
          text: `${count} record${count > 1 ? "s" : ""} to review`
        })
      }

      if ((count = statusInfos["HISTORICAL_MATCH"])) {
        result.push({
          class: "statusalert",
          text: `${count} confirmed match${count > 1 ? "es" : ""}`
        })
      }

      if (!result.length) {
        return [{ class: "statussuccess", text: "All records cleared" }]
      }

      return result
    },
    groupUniqueId(groupKey) {
      return this.groups[groupKey].uniqueId
    },
    groupSearchCriteria(groupKey) {
      return this.groups[groupKey].searchCriteria
    },

    _updateGroups({ scGroups }) {
      this.groups = Object.assign(this.groups, scGroups)
    },

    reset() {
      // reset search filters
      this.searchCriteriaFilter = ""
      this.statusFilter = []
      this.sourcesFilter = []
      this.debouncedSearch(true) // search is not triggered automatically as a result of model change
    },

    debouncedSearch: debounce(function(newSearch) {
      thePromise.then(() => this.search(newSearch))
    }, SEARCH_DEBOUNCE_TIMEOUT),

    initPage() {
      this.page = 1
      this.selectedItems = []
    },
    search(newSearch = false) {
      this.loading = true

      if (newSearch) {
        this.initPage()
      }

      const page = this.page
      console.log(
        `Fetching flexible batch search details page: ${page} for batchId: ${
          this.batchId
        }, newSearch: ${newSearch}`
      )

      return axios
        .get(API.FLEXBATCH_SANCTION_DETAILS.replace(":batchId", this.batchId), {
          params: {
            limits: RECORDS_PER_PAGE,
            page,
            sort: sortName(this.searchCriteriaSortDescending).toUpperCase(),
            requireTotal: newSearch,

            sc: this.searchCriteriaFilter,
            status: this.statusFilter,
            sources: this.sourcesFilter
          }
        })
        .then(response => {
          this.groupSortDescending = this.searchCriteriaSortDescending

          if (newSearch)
            this.pages = Math.ceil(response.data.total / RECORDS_PER_PAGE)

          this._updateGroups(response.data)

          if (response.data.records) this.items = response.data.records
          if (!this.isTableInit) {
            this.updateFixedTable()
            this.isTableInit = true
          }
        })
        .finally(() => (this.loading = false))
    },

    updateSlideIn(id) {
      this.currentSlideInId = id
      axios
        .get(
          API.FLEXBATCH_SANCTION_DETAILS_BYID.replace(":id", id).replace(
            ":batchId",
            this.batchId
          )
        )
        .then(response => {
          this.setSlideIn(response.data)
          this.setStatusInfos(nameStatusInfos)
          this.hasClearNonBtn(false)
          this.setEventModifier(eventModifier)
        })
    },
    updateSelectedHeaderValues({ value }) {
      const arry = this.selectedHeaderValues
      deleteElFromArry(arry, value) || arry.push(value)

      this.updateFixedTable()
    },

    updateFixedTable() {
      const overflowWrapper = document.querySelector(".exclusion-table")

      if (overflowWrapper) {
        const totalWidth = this.selectedHeaders.reduce(
          (acc, { width }) => acc + parseInt(width),
          0
        )
        this.fixedColumnsTable = totalWidth > overflowWrapper.clientWidth
      }
    },

    isHeaderSelected(value) {
      return this.selectedHeaderValues.includes(value)
    }
  },
  filters: {
    upper,
    sortName
  },

  computed: {
    statusFilterOptions() {
      return createFilterList(this.nameStatusInfos)
    },
    filteredSelectedHeaders() {
      const selectedHeaders = this.selectedHeaders
      if (!this.fixedColumnsTable) selectedHeaders.splice(0, 1)

      return selectedHeaders
    },
    selectedHeaders() {
      const fixedHeaders = [
        {
          text: "",
          fixed: true,
          width: "40px"
        },
        {
          value: "name",
          fixed: this.fixedColumnsTable,
          width: "180px",
          text: "Name"
        },
        {
          value: "companyName",
          fixed: this.fixedColumnsTable,
          width: "200px",
          text: "Company Name"
        }
      ]
      return fixedHeaders.concat(
        availableHeaders.filter(({ value }) => this.isHeaderSelected(value))
      )
    },

    ...mapState({
      sources: state => state.util.sources // array syntax does not work
    })
  },
  components: { StatusDropdownDialog, EcDropdown }
}
</script>

<style lang="scss" scoped>
.flex-search-details-exclusions {
  padding: 2.25rem;
  padding-bottom: 1rem;

  .exclusion-table ::v-deep .v-datatable {
    > thead {
      tr:first-child {
        th:nth-child(-n + 3) {
          background-color: $color-ec-dark-blue;
        }
      }
    }
  }
  table.groupinfotable {
    text-align: left;
    border-collapse: collapse;
    tr {
      border-bottom: none;
      &:hover {
        background-color: transparent;
      }
    }
    th {
      color: $color-ec-dark2-gray;
      padding: 0;
      font-size: 0.625rem;
      height: 0.625rem;

      &:nth-child(2) {
        @include shorten-left-border(true);
      }
    }
    td {
      padding: 0;
      @include label-font;
      height: 0.875rem;

      &:nth-child(2) {
        @include shorten-left-border(false);
      }
    }
    th,
    td {
      &:first-child:not(.last-column) {
        padding-right: 1rem;
      }
      &:last-child {
        padding-left: 1rem;
      }
    }
  }
}
</style>

<style lang="scss">
.app-select-options {
  border: 1px solid $color-ec-blue;
  .v-list__tile {
    &:hover {
      text-decoration: none;
    }
    height: 2rem;
  }
  @include ec-checkbox-addon;

  .v-input__slot {
    margin-bottom: 0;
  }
  .v-list__tile__action {
    min-width: auto;
    margin-right: 0.75rem;
  }
  .v-list__tile__content {
    font-size: 0.875rem;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/FlexSearchDetails-Exclusions.vue